import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const WhatsAppIcon = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

import suiteAutomotive from '../../assets/whatsapp_suites/automotive.webp';
import suiteEcommerce from '../../assets/whatsapp_suites/ecommerce.webp';
import suiteEducation from '../../assets/whatsapp_suites/education.webp';
import suiteHealthcare from '../../assets/whatsapp_suites/healthcare.webp';
import suiteItTech from '../../assets/whatsapp_suites/it_and_tech.webp';
import suiteNonProfit from '../../assets/whatsapp_suites/non_profit.webp';
import suiteRealEstate from '../../assets/whatsapp_suites/real_estate.webp';
import suiteTravel from '../../assets/whatsapp_suites/travel.webp';

interface SuiteSlide {
  image: string;
  title: string;
  description: string;
  tag: string;
}

const DEFAULT_SLIDES: SuiteSlide[] = [
  {
    image: suiteEcommerce,
    title: 'E-Commerce',
    description: 'Send order confirmations, shipping updates, and personalized product recommendations via WhatsApp to boost repeat purchases.',
    tag: 'Retail & Shopping',
  },
  {
    image: suiteHealthcare,
    title: 'Healthcare',
    description: 'Automate appointment reminders, share lab reports, and send health tips to patients — all through secure WhatsApp messaging.',
    tag: 'Medical & Wellness',
  },
  {
    image: suiteEducation,
    title: 'Education',
    description: 'Engage students and parents with admission updates, fee reminders, exam schedules, and instant campus announcements.',
    tag: 'Schools & Universities',
  },
  {
    image: suiteNonProfit,
    title: 'Non-Profit',
    description: 'Keep donors informed with impact reports, fundraising campaigns, and volunteer coordination — directly on WhatsApp.',
    tag: 'NGOs & Foundations',
  },
  {
    image: suiteRealEstate,
    title: 'Real Estate',
    description: 'Share property listings, schedule viewings, and follow up with buyers using rich media messages and quick replies.',
    tag: 'Property & Housing',
  },
  {
    image: suiteItTech,
    title: 'IT & Technology',
    description: 'Deliver service alerts, onboarding guides, and support ticket updates to clients through automated WhatsApp workflows.',
    tag: 'Software & SaaS',
  },
  {
    image: suiteAutomotive,
    title: 'Automotive',
    description: 'Send service reminders, new model launches, and test drive bookings to customers with rich WhatsApp catalogs.',
    tag: 'Cars & Vehicles',
  },
  {
    image: suiteTravel,
    title: 'Travel & Hospitality',
    description: 'Manage bookings, send itineraries, and share travel advisories with guests through instant WhatsApp notifications.',
    tag: 'Hotels & Tourism',
  },
];

interface WhatsAppSuiteSliderProps {
  title?: string;
  subtitle?: string;
  slides?: SuiteSlide[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
  className?: string;
}

const WhatsAppSuiteSlider = ({
  title = 'Built for Every Industry',
  subtitle = 'One Platform, Endless Possibilities',
  slides = DEFAULT_SLIDES,
  autoPlay = true,
  autoPlayInterval = 5000,
  className = '',
}: WhatsAppSuiteSliderProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pausedRef = useRef(false);

  const goTo = useCallback((index: number, dir: number) => {
    setDirection(dir);
    setActiveIndex(index);
  }, []);

  const next = useCallback(() => {
    setActiveIndex(prev => {
      setDirection(1);
      return (prev + 1) % slides.length;
    });
  }, [slides.length]);

  const prev = useCallback(() => {
    setActiveIndex(prev => {
      setDirection(-1);
      return (prev - 1 + slides.length) % slides.length;
    });
  }, [slides.length]);

  useEffect(() => {
    if (!autoPlay) return;
    const start = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        if (!pausedRef.current) next();
      }, autoPlayInterval);
    };
    start();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoPlay, autoPlayInterval, next]);

  const pauseAutoPlay = useCallback(() => { pausedRef.current = true; }, []);
  const resumeAutoPlay = useCallback(() => { pausedRef.current = false; }, []);

  const slide = slides[activeIndex];

  const slideVariants = {
    enter: (d: number) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d > 0 ? '-100%' : '100%', opacity: 0 }),
  };

  return (
    <section
      className={`py-20 lg:py-28 bg-linear-to-b from-[#0a0f1a] to-(--color-dark-navy) overflow-hidden relative ${className}`}
      onMouseEnter={pauseAutoPlay}
      onMouseLeave={resumeAutoPlay}
    >
      {/* Floating WhatsApp background icons */}
      <motion.div
        className="absolute top-16 left-[6%] hidden lg:block"
        animate={{ y: [-12, 12, -12], rotate: [-6, 6, -6] }}
        transition={{ duration: 7, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
      >
        <div className="p-3 rounded-2xl bg-[#25D366]/8 border border-[#25D366]/15">
          <WhatsAppIcon size={22} className="text-[#25D366]" />
        </div>
      </motion.div>
      <motion.div
        className="absolute bottom-24 right-[7%] hidden lg:block"
        animate={{ y: [10, -10, 10], rotate: [4, -4, 4] }}
        transition={{ duration: 8, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95], delay: 1.5 }}
      >
        <div className="p-3 rounded-2xl bg-[#25D366]/8 border border-[#25D366]/15">
          <WhatsAppIcon size={18} className="text-[#25D366]" />
        </div>
      </motion.div>
      <motion.div
        className="absolute top-1/2 right-[3%] hidden xl:block opacity-[0.04]"
        animate={{ y: [-8, 8, -8] }}
        transition={{ duration: 9, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95], delay: 0.5 }}
      >
        <WhatsAppIcon size={100} className="text-[#25D366]" />
      </motion.div>

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          className="text-center max-w-3xl mx-auto mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#25D366]/10 border border-[#25D366]/20 text-xs font-semibold uppercase tracking-widest text-[#25D366] mb-5"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <WhatsAppIcon size={14} className="text-[#25D366]" />
            Industry Solutions
          </motion.span>
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-white mb-3 leading-tight">
            {title}
          </h2>
          <p className="text-xl lg:text-2xl font-heading font-medium text-gradient">
            {subtitle}
          </p>
          <p className="text-(--color-muted-gray) text-base mt-4 max-w-xl mx-auto">
            From healthcare to e-commerce, AmplifyMsg adapts to your workflow — so you can focus on growing your business.
          </p>
        </motion.div>

        {/* Slider */}
        <div className="relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[400px]">
            {/* Image side */}
            <div className="relative aspect-4/3 rounded-2xl overflow-hidden bg-black/20">
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.img
                  key={activeIndex}
                  src={slide.image}
                  alt={slide.title}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.45, ease: 'easeInOut' }}
                  className="absolute inset-0 w-full h-full object-cover rounded-2xl"
                />
              </AnimatePresence>

              {/* Tag overlay */}
              <div className="absolute top-4 left-4 z-10">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-(--color-signal-orange) text-white shadow-lg">
                  {slide.tag}
                </span>
              </div>
            </div>

            {/* Content side */}
            <div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.35 }}
                >
                  <span className="text-xs font-bold uppercase tracking-widest text-(--color-signal-orange) mb-3 block">
                    {String(activeIndex + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                  </span>
                  <h3 className="text-2xl lg:text-4xl font-heading font-bold text-white mb-4">
                    {slide.title}
                  </h3>
                  <p className="text-(--color-soft-gray) text-base lg:text-lg leading-relaxed mb-8">
                    {slide.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Controls */}
              <div className="flex items-center gap-4">
                <button
                  onClick={prev}
                  className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:bg-(--color-signal-orange) hover:border-(--color-signal-orange) hover:text-white transition-all duration-200 cursor-pointer"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={next}
                  className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:bg-(--color-signal-orange) hover:border-(--color-signal-orange) hover:text-white transition-all duration-200 cursor-pointer"
                >
                  <ChevronRight size={20} />
                </button>

                {/* Progress dots */}
                <div className="flex items-center gap-2 ml-4">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i, i > activeIndex ? 1 : -1)}
                      className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                        i === activeIndex
                          ? 'w-8 bg-(--color-signal-orange)'
                          : 'w-2 bg-white/20 hover:bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { WhatsAppSuiteSlider, type SuiteSlide, type WhatsAppSuiteSliderProps };
